import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  TextField,
  MenuItem,
  Button,
  Divider,
  Chip,
  CircularProgress,
  Alert,
  useTheme,
  Stepper,
  Step,
  StepLabel,
  Paper,
  Fade,
  Grow,
  LinearProgress,
  Avatar,
  IconButton,
  Tooltip,
  Container,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  FormControl,
  InputLabel,
  Select
} from '@mui/material';
import {
  Assessment,
  TrendingUp,
  Calculate,
  CheckCircle,
  ExpandMore,
  Refresh,
  PlayArrow,
  Settings,
  Analytics,
  BarChart,
  PieChart,
  Timeline,
  Speed,
  VerifiedUser,
  Security,
  AccountBalance
} from '@mui/icons-material';
import axios from 'axios';

interface CategoryItem {
  id: number;
  name: string;
  code: string;
  category_id: number;
}

interface Category {
  id: number;
  name: string;
  code: string;
  items: CategoryItem[];
}

interface RiskFactor {
  component: string;
  display_name: string;
  intercept: number;
  coefficient: number;
  control_value: number;
  implied_pd: number;
  weight: number;
  weighted_pd: number;
}

interface ComponentWeight {
  component: string;
  display_name: string;
  weight: number;
  default_weight: number;
}

const RiskAssessment: React.FC = () => {
  const theme = useTheme();
  const [categories, setCategories] = useState<Category[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedValues, setSelectedValues] = useState<Record<string, string>>({});
  const [riskFactors, setRiskFactors] = useState<RiskFactor[]>([]);
  const [judgmentalValue, setJudgmentalValue] = useState<number>(3);
  const [judgmentWeight, setJudgmentWeight] = useState<number>(25); // 25% default
  const [totalWeightedPd, setTotalWeightedPd] = useState<number>(0);
  const [finalPd, setFinalPd] = useState<number>(0);
  const [judgmentalMultiplier, setJudgmentalMultiplier] = useState<number>(1.1);
  const [calculating, setCalculating] = useState(false);
  // No default coefficients - must be loaded from API
  const [regressionCoefficients, setRegressionCoefficients] = useState<Record<string, { intercept: number; coefficient: number }>>({});

  const [componentWeights, setComponentWeights] = useState<Record<string, number>>({});

  // Year selection
  const [yearFilter, setYearFilter] = useState<number | ''>('');
  const [availableYears, setAvailableYears] = useState<number[]>([]);

  // Step completion tracking
  const [stepCompleted, setStepCompleted] = useState<Record<number, boolean>>({
    0: false, // Risk Factors
    1: false, // Component Weights
    2: false, // Implied PDs
    3: false, // Judgmental Adjustment
    4: false  // Results
  });

  useEffect(() => {
    fetchCategories();
    fetchAvailableYears();
  }, []);

  // Load data when available years are fetched
  useEffect(() => {
    if (availableYears.length > 0 && !yearFilter) {
      // Auto-select the most recent year
      const latestYear = Math.max(...availableYears);
      setYearFilter(latestYear);
    }
  }, [availableYears]);

  const fetchAvailableYears = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/master-data', {
        headers: { Authorization: `Bearer ${token}` },
        params: { per_page: 1000 }
      });
      if (response.data?.data) {
        const yearsSet = new Set<number>();
        response.data.data.forEach((item: any) => { yearsSet.add(item.year); });
        const years = Array.from(yearsSet).sort((a, b) => b - a);
        setAvailableYears(years);
        if (years.length > 0 && !yearFilter) {
          setYearFilter(years[0]);
        }
      }
    } catch (error) {
      console.error('Error fetching available years:', error);
    }
  };

  const fetchRatingComponents = async (year: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/v1/analytics/rating-components?year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.components) {
        // Map the weights from rating components
        const newWeights: Record<string, number> = {};
        response.data.components.forEach((comp: any) => {
          // Convert component name to key format
          const componentKey = comp.component.toLowerCase();
          newWeights[componentKey] = comp.weights;
        });
        setComponentWeights(newWeights);
      }
    } catch (error) {
      console.error('Error fetching rating components:', error);
      // Weights will remain empty - must be loaded from API
    }
  };

  const fetchRegressionCoefficients = async (year: number) => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `/api/v1/analytics/regression-analysis?year=${year}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      if (response.data?.results) {
        // Map the coefficients from regression analysis
        const newCoefficients: Record<string, { intercept: number; coefficient: number }> = {};
        
        Object.entries(response.data.results).forEach(([category, result]: [string, any]) => {
          if (result?.coefficients) {
            const categoryKey = category.toLowerCase();
            newCoefficients[categoryKey] = {
              intercept: result.coefficients.intercept.value,
              coefficient: result.coefficients.slope.value
            };
          }
        });
        
        if (Object.keys(newCoefficients).length > 0) {
          setRegressionCoefficients(newCoefficients);
        }
      }
    } catch (error) {
      console.error('Error fetching regression coefficients:', error);
      // Keep default coefficients if fetch fails
    }
  };

  // Fetch rating components and regression coefficients when year changes
  useEffect(() => {
    if (yearFilter) {
      fetchRatingComponents(yearFilter as number);
      fetchRegressionCoefficients(yearFilter as number);
    }
  }, [yearFilter]);

  // Build component key to category name mapping from categories data
  const findCategoryForComponent = (componentKey: string): Category | undefined => {
    const componentKeyLower = componentKey.toLowerCase();
    const keyWithoutUnderscore = componentKeyLower.replace(/_/g, ' ');
    const keyWithDash = componentKeyLower.replace(/_/g, '-');
    
    // Try to find a category whose name contains the component key
    return categories.find(category => {
      const categoryNameLower = category.name.toLowerCase();
      
      // Direct match with underscore replaced
      if (categoryNameLower.includes(keyWithoutUnderscore)) return true;
      
      // Match with dash
      if (categoryNameLower.includes(keyWithDash)) return true;
      
      // Match with just the key words
      const keyWords = keyWithoutUnderscore.split(' ');
      return keyWords.some(word => word.length > 2 && categoryNameLower.includes(word));
    });
  };

  // Dynamic PD calculation whenever selections change
  useEffect(() => {
    if (categories.length > 0 && Object.keys(selectedValues).length === categories.length) {
      calculateDynamicPDs();
    }
  }, [selectedValues, categories, componentWeights, judgmentalValue, judgmentWeight, findCategoryForComponent]);

  const calculateDynamicPDs = () => {
    try {
      const factors: RiskFactor[] = [];

      // Calculate PD for each component
      Object.entries(componentWeights).forEach(([componentKey, weight]) => {
        // Find the category that matches this component key
        const category = findCategoryForComponent(componentKey);
        
        if (!category) {
          // Skip if no matching category found
          return;
        }

        // Get the selected value for this category
        const selectedValue = selectedValues[category.name] || '';
        const selectedItem = category.items.find(item => item.name === selectedValue);
        const controlValue = selectedItem ? parseInt(selectedItem.code) : 0;

        // Get coefficients
        const coeffs = regressionCoefficients[componentKey] || { intercept: 0, coefficient: 0 };

        // Calculate PD: intercept + coefficient × control_value
        const impliedPd = coeffs.intercept + (coeffs.coefficient * controlValue);
        const weightedPd = impliedPd * weight;

        factors.push({
          component: componentKey,
          display_name: category.name,
          intercept: coeffs.intercept,
          coefficient: coeffs.coefficient,
          control_value: controlValue,
          implied_pd: impliedPd,
          weight: weight,
          weighted_pd: weightedPd
        });
      });

      setRiskFactors(factors);

      // Calculate total weighted PD
      const totalWeighted = factors.reduce((sum, factor) => sum + factor.weighted_pd, 0);

      // Apply scaling factor (same as backend)
      const scalingFactor = 2.284;
      const adjustedTotalWeighted = totalWeighted / scalingFactor;

      // Apply judgmental adjustment
      const finalPdValue = adjustedTotalWeighted * judgmentalMultiplier;

      setTotalWeightedPd(adjustedTotalWeighted);
      setFinalPd(finalPdValue);

      // Mark steps as completed
      if (!stepCompleted[2]) markStepCompleted(2); // Implied PDs
      if (!stepCompleted[3]) markStepCompleted(3); // Judgmental Adjustment
      if (!stepCompleted[4]) markStepCompleted(4); // Results

    } catch (error) {
      console.error('Error calculating dynamic PDs:', error);
    }
  };

  const fetchCategories = async () => {
    try {
      setLoading(true);
      setError(null);

      const token = localStorage.getItem('token');
      const response = await axios.get('/api/v1/input/categories', {
        headers: { Authorization: `Bearer ${token}` }
      });

      setCategories(response.data);
      setLoading(false);

      // Initialize selected values with first item from each category
      const initialValues: Record<string, string> = {};
      response.data.forEach((category: Category) => {
        if (category.items && category.items.length > 0) {
          initialValues[category.name] = category.items[0].name;
        }
      });
      setSelectedValues(initialValues);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to fetch categories');
      setLoading(false);
    }
  };

  const handleValueChange = (categoryName: string, value: string) => {
    setSelectedValues(prev => ({
      ...prev,
      [categoryName]: value
    }));

    // Check if all categories are selected to mark step 1 complete
    const updatedValues = { ...selectedValues, [categoryName]: value };
    const allSelected = categories.every(cat => updatedValues[cat.name]);
    if (allSelected) {
      markStepCompleted(0);
    }
  };

  const handleJudgmentalChange = (value: number) => {
    setJudgmentalValue(value);
    updateJudgmentalMultiplier(value, judgmentWeight);
  };

  const handleJudgmentWeightChange = (value: string) => {
    const numValue = parseFloat(value) || 0;
    setJudgmentWeight(numValue);
    updateJudgmentalMultiplier(judgmentalValue, numValue);
  };

  const updateJudgmentalMultiplier = (judgmentValue: number, weight: number) => {
    let multiplier = 1.0;
    const weightDecimal = weight / 100; // Convert percentage to decimal
    if (judgmentValue === 1) {
      multiplier = 1.0; // Neutral - no adjustment
    } else if (judgmentValue === 2) {
      multiplier = 1 - weightDecimal; // Positive - reduce PD
    } else if (judgmentValue === 3) {
      multiplier = 1 + weightDecimal; // Negative - increase PD
    }
    setJudgmentalMultiplier(multiplier);
  };

  const handleWeightChange = (component: string, value: string) => {
    const numValue = parseFloat(value) || 0;
    setComponentWeights(prev => ({
      ...prev,
      [component]: numValue
    }));
  };

  const steps = ['Risk Factors', 'Component Weights', 'Implied PDs', 'Judgmental Adjustment', 'Results'];
  const [activeStep, setActiveStep] = useState(0);
  const [impliedPDsCalculated, setImpliedPDsCalculated] = useState(false);

  const getStepIcon = (step: number) => {
    // Show checkmark for completed steps
    if (stepCompleted[step]) {
      return <CheckCircle />;
    }

    const icons = [<Assessment />, <Settings />, <Timeline />, <VerifiedUser />, <BarChart />];
    return icons[step] || <Assessment />;
  };

  const handleNext = () => {
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (step: number) => {
    // Only allow navigation to steps that are accessible
    if (canAccessStep(step)) {
      setActiveStep(step);
    }
  };

  const canAccessStep = (step: number): boolean => {
    // Step 1 (Risk Factors) is always accessible
    if (step === 0) return true;

    // Step 2 (Component Weights) - accessible after step 1 is completed
    if (step === 1) return stepCompleted[0];

    // Step 3 (Implied PDs) - accessible after step 2 is completed
    if (step === 2) return stepCompleted[1];

    // Step 4 (Judgmental Adjustment) - accessible after step 3 is completed
    if (step === 3) return stepCompleted[2];

    // Step 5 (Results) - accessible after step 4 is completed
    if (step === 4) return stepCompleted[3];

    return false;
  };

  const markStepCompleted = (step: number) => {
    setStepCompleted(prev => ({
      ...prev,
      [step]: true
    }));
  };

  const calculateImpliedPDs = async () => {
    try {
      setCalculating(true);
      setError(null);

      // Prepare controls object for API
      const controls: Record<string, number> = {};

      // Get codes for selected values - use dynamic mapping from categories
      categories.forEach(category => {
        const selectedItem = category.items.find(item => item.name === selectedValues[category.name]);
        if (selectedItem) {
          // Find matching component key for this category
          const categoryMatch = findCategoryForComponent(category.name);
          const componentKey = categoryMatch ? 
            Object.entries({age: 'age', nationality: 'nationality', marital: 'marital', dependents: 'dependents', education: 'education', occupation: 'occupation', job_type: 'job_type', service_current: 'service_current', service_uae: 'service_uae', company_listing: 'company_listing', monthly_salary: 'monthly_salary', post_money_dsr: 'post_money_dsr'}).find(
              ([key, value]) => key === category.name.toLowerCase().replace(/\s+/g, '_') || value === category.name.toLowerCase().replace(/\s+/g, '_')
            )?.[0] || category.name.toLowerCase().replace(/\s+/g, '_') : category.name.toLowerCase().replace(/\s+/g, '_');
          
          if (componentKey) {
            controls[componentKey] = parseInt(selectedItem.code);
          }
        }
      });

      // Add judgmental control
      controls.judgmental = judgmentalValue;

      const token = localStorage.getItem('token');
      const response = await axios.post('/api/v1/analytics/implied-pds', {
        controls: controls,
        component_weights: componentWeights,
        judgment_weight: judgmentWeight
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });

      // Transform response data for display
      const factors: RiskFactor[] = response.data.components.map((component: any) => ({
        component: component.component,
        display_name: component.display_name,
        intercept: component.intercept,
        coefficient: component.coefficient,
        control_value: component.control_value,
        implied_pd: component.implied_pd,
        weight: component.weight,
        weighted_pd: component.weighted_pd
      }));

      setRiskFactors(factors);
      setTotalWeightedPd(response.data.total_weighted_pd);
      setFinalPd(response.data.final_pd);
      setJudgmentalMultiplier(response.data.judgmental_adjustment);

      setCalculating(false);

    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to calculate implied PDs');
      setCalculating(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mt: 3 }}>
        {error}
      </Alert>
    );
  }

  return (
    <Container maxWidth="xl">
      {/* Hero Header */}
      <Fade in timeout={1000}>
        <Paper
          elevation={0}
          sx={{
            p: 4,
            mb: 4,
            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
            color: 'white',
            borderRadius: 3,
            position: 'relative',
            overflow: 'hidden',
            '&::before': {
              content: '""',
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: 'url("data:image/svg+xml,%3Csvg width="60" height="60" viewBox="0 0 60 60" xmlns="http://www.w3.org/2000/svg"%3E%3Cg fill="none" fill-rule="evenodd"%3E%3Cg fill="%23ffffff" fill-opacity="0.05"%3E%3Ccircle cx="30" cy="30" r="4"/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
            }
          }}
        >
          <Box sx={{ position: 'relative', zIndex: 1 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
              <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 2 }}>
                <AccountBalance />
              </Avatar>
              <Box>
                <Typography variant="h3" sx={{ fontWeight: 700, mb: 1 }}>
                  Credit Risk Assessment
                </Typography>
                <Typography variant="h6" sx={{ opacity: 0.9, fontWeight: 400 }}>
                  Advanced Probability of Default Calculator
                </Typography>
              </Box>
            </Box>
            <Typography variant="body1" sx={{ opacity: 0.8, maxWidth: 600 }}>
              Leverage our sophisticated CRR model to calculate implied probability of default
              using statistical regression analysis and expert judgmental adjustments.
            </Typography>
          </Box>
        </Paper>
      </Fade>

      {/* Progress Stepper */}
      <Fade in timeout={1500}>
        <Paper sx={{ p: 3, mb: 4, borderRadius: 2 }}>
          <Stepper activeStep={activeStep} alternativeLabel>
            {steps.map((label, index) => (
              <Step key={label}>
                <StepLabel
                  StepIconComponent={() => (
                    <Avatar
                      sx={{
                        bgcolor: activeStep >= index ? 'primary.main' : 'grey.300',
                        color: activeStep >= index ? 'white' : 'grey.600',
                        transition: 'all 0.3s ease'
                      }}
                    >
                      {getStepIcon(index)}
                    </Avatar>
                  )}
                  sx={{
                    cursor: 'pointer',
                    '&:hover': { opacity: 0.8 }
                  }}
                  onClick={() => handleStepClick(index)}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    {label}
                  </Typography>
                </StepLabel>
              </Step>
            ))}
          </Stepper>
        </Paper>
      </Fade>

      {/* Step Content */}
      <Grow in timeout={activeStep * 200 + 1000}>
        <Box>
          {/* Step 1: Risk Factors */}
          {activeStep === 0 && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                <Assessment sx={{ mr: 1, verticalAlign: 'middle' }} />
                Step 1: Select Risk Factor Values
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Choose appropriate values for each risk factor based on the applicant's profile
              </Typography>

              {/* Year Selection */}
              <Card sx={{ mb: 4, background: 'linear-gradient(135deg, #f5f7fa 0%, #e4e8eb 100%)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, flexWrap: 'wrap' }}>
                    <FormControl sx={{ minWidth: 200 }}>
                      <InputLabel>Select Year</InputLabel>
                      <Select
                        value={yearFilter}
                        label="Select Year"
                        onChange={(e) => setYearFilter(e.target.value as number)}
                      >
                        {availableYears.map((year) => (
                          <MenuItem key={year} value={year}>
                            {year}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                    {yearFilter && (
                      <Chip 
                        label={`Loading weights from Rating Components for ${yearFilter}`}
                        color="info"
                        variant="outlined"
                      />
                    )}
                  </Box>
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 2 }}>
                    Component weights will be loaded from Rating Components for the selected year.
                  </Typography>
                </CardContent>
              </Card>

              <Grid container spacing={3}>
                {categories.map((category, index) => (
                  <Grid item xs={12} sm={6} md={4} key={category.id}>
                    <Grow in timeout={index * 100 + 500}>
                      <Card
                        sx={{
                          height: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-4px)',
                            boxShadow: theme.shadows[8],
                            borderColor: 'primary.main'
                          },
                          border: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <CardContent sx={{ p: 3 }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                            <Avatar sx={{ bgcolor: 'primary.light', mr: 2, width: 32, height: 32 }}>
                              <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                                {index + 1}
                              </Typography>
                            </Avatar>
                            <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                              {category.name}
                            </Typography>
                          </Box>

                          <TextField
                            select
                            label="Select Value"
                            value={selectedValues[category.name] || ''}
                            onChange={(e) => handleValueChange(category.name, e.target.value)}
                            fullWidth
                            variant="outlined"
                            sx={{
                              mb: 2,
                              '& .MuiOutlinedInput-root': {
                                '&:hover fieldset': {
                                  borderColor: 'primary.main',
                                },
                              }
                            }}
                          >
                            {category.items.map((item) => (
                              <MenuItem key={item.id} value={item.name}>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                                  <Typography>{item.name}</Typography>
                                  <Chip
                                    label={`Code: ${item.code}`}
                                    size="small"
                                    variant="outlined"
                                    sx={{ ml: 1 }}
                                  />
                                </Box>
                              </MenuItem>
                            ))}
                          </TextField>

                          {selectedValues[category.name] && (
                            <Fade in>
                              <Chip
                                label={`✓ ${selectedValues[category.name]}`}
                                color="success"
                                size="small"
                                sx={{ fontWeight: 600 }}
                              />
                            </Fade>
                          )}
                        </CardContent>
                      </Card>
                    </Grow>
                  </Grid>
                ))}
              </Grid>
            </Box>
          )}

          {/* Step 2: Component Weights */}
          {activeStep === 1 && (() => {
            // Mark step 2 as completed when entering (weights are always available)
            if (!stepCompleted[1]) {
              markStepCompleted(1);
            }
            return (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                <Settings sx={{ mr: 1, verticalAlign: 'middle' }} />
                Step 2: Adjust Component Weights
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Fine-tune the relative importance of each risk factor in your assessment model
              </Typography>

              <Card sx={{ mb: 3, background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
                <CardContent sx={{ p: 3 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                    <TrendingUp sx={{ mr: 2, color: 'primary.main' }} />
                    <Typography variant="h6" sx={{ fontWeight: 600 }}>
                      Weight Distribution Overview
                    </Typography>
                  </Box>
                  <LinearProgress
                    variant="determinate"
                    value={Math.min((Object.values(componentWeights).reduce((sum, w) => sum + w, 0) * 100), 100)}
                    sx={{
                      height: 8,
                      borderRadius: 4,
                      mb: 2,
                      backgroundColor: 'grey.300',
                      '& .MuiLinearProgress-bar': {
                        backgroundColor: 'primary.main',
                      }
                    }}
                  />
                  <Typography variant="body2" color="text.secondary">
                    Total Weight: {(Object.values(componentWeights).reduce((sum, w) => sum + w, 0) * 100).toFixed(2)}%
                    {Object.values(componentWeights).reduce((sum, w) => sum + w, 0) > 1 && (
                      <span style={{ color: 'error.main', fontWeight: 600 }}> (Over 100% - Please adjust)</span>
                    )}
                  </Typography>
                </CardContent>
              </Card>

              <Grid container spacing={3}>
                {Object.entries(componentWeights).map(([component, weight], index) => {
                  // Use exact category name from database (Descriptive Data)
                  const category = findCategoryForComponent(component);
                  const displayName = category?.name || component.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
                  return (
                    <Grid item xs={12} sm={6} md={4} key={component}>
                      <Grow in timeout={index * 50 + 500}>
                        <Card
                          sx={{
                            transition: 'all 0.3s ease',
                            '&:hover': {
                              transform: 'translateY(-2px)',
                              boxShadow: theme.shadows[4]
                            }
                          }}
                        >
                          <CardContent sx={{ p: 3 }}>
                            <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 2, color: 'primary.main' }}>
                              {displayName}
                            </Typography>
                            <TextField
                              type="number"
                              value={weight}
                              onChange={(e) => handleWeightChange(component, e.target.value)}
                              fullWidth
                              size="small"
                              inputProps={{
                                step: 0.0001,
                                min: 0,
                                max: 1
                              }}
                              sx={{
                                '& input': {
                                  fontFamily: 'monospace',
                                  fontSize: '0.9rem'
                                }
                              }}
                            />
                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                              {(weight * 100).toFixed(2)}% of total weight
                            </Typography>
                          </CardContent>
                        </Card>
                      </Grow>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
            );
          })()}

          {/* Step 3: Implied PDs */}
          {activeStep === 2 && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
                Step 3: Calculate Implied PDs
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Calculate implied probability of default for each risk factor using CRR model regression coefficients
              </Typography>

              {!impliedPDsCalculated ? (
                <Card sx={{ p: 6, textAlign: 'center', background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                  <Timeline sx={{ fontSize: 80, mb: 2, opacity: 0.7 }} />
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Ready to Calculate Implied PDs
                  </Typography>
                  <Typography variant="body1" sx={{ opacity: 0.9, mb: 3 }}>
                    Click the button below to calculate implied PDs for each risk factor using the selected values and weights
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={async () => {
                      await calculateImpliedPDs();
                      setImpliedPDsCalculated(true);
                      markStepCompleted(2); // Mark step 3 (Implied PDs) as completed
                    }}
                    disabled={calculating}
                    startIcon={calculating ? <CircularProgress size={20} /> : <Calculate />}
                    sx={{
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      borderRadius: 3,
                      backgroundColor: 'rgba(255,255,255,0.2)',
                      '&:hover': {
                        backgroundColor: 'rgba(255,255,255,0.3)',
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[8]
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {calculating ? 'Calculating PDs...' : 'Calculate Implied PDs'}
                  </Button>
                </Card>
              ) : (
                <Fade in timeout={1000}>
                  <Box>
                    <Alert severity="success" sx={{ mb: 3 }}>
                      <strong>Implied PDs calculated successfully!</strong> Review the results below and proceed to apply judgmental adjustments.
                    </Alert>

                    <Grid container spacing={3}>
                      {riskFactors.map((factor, index) => (
                        <Grid item xs={12} sm={6} md={4} key={factor.component}>
                          <Grow in timeout={index * 100 + 500}>
                            <Card
                              sx={{
                                height: '100%',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  boxShadow: theme.shadows[8],
                                  borderColor: 'primary.main'
                                },
                                border: '1px solid',
                                borderColor: 'divider'
                              }}
                            >
                              <CardContent sx={{ p: 3 }}>
                                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                  <Avatar sx={{ bgcolor: 'primary.light', mr: 2, width: 32, height: 32 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, color: 'primary.contrastText' }}>
                                      {index + 1}
                                    </Typography>
                                  </Avatar>
                                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                                    {factor.display_name}
                                  </Typography>
                                </Box>

                                <Box sx={{ mb: 2 }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Control Value: {factor.control_value}
                                  </Typography>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Formula: {factor.intercept.toFixed(6)} + ({factor.coefficient.toFixed(6)} × {factor.control_value})
                                  </Typography>
                                </Box>

                                <Divider sx={{ my: 2 }} />

                                <Box sx={{ textAlign: 'center' }}>
                                  <Typography variant="h5" sx={{ fontWeight: 700, color: 'primary.main', fontFamily: 'monospace' }}>
                                    {(factor.implied_pd * 100).toFixed(4)}%
                                  </Typography>
                                  <Typography variant="caption" color="text.secondary">
                                    Raw PD: {factor.implied_pd.toFixed(6)}
                                  </Typography>
                                </Box>

                                <Box sx={{ mt: 2, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                                    Weight: {(factor.weight * 100).toFixed(2)}%
                                  </Typography>
                                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                    Weighted PD: {factor.weighted_pd.toFixed(6)}
                                  </Typography>
                                </Box>
                              </CardContent>
                            </Card>
                          </Grow>
                        </Grid>
                      ))}
                    </Grid>

                    <Card sx={{ mt: 4, background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', color: 'white' }}>
                      <CardContent sx={{ p: 4, textAlign: 'center' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                          Total Weighted PD Calculation
                        </Typography>
                        <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'monospace', mb: 1 }}>
                          {totalWeightedPd.toFixed(6)}
                        </Typography>
                        <Typography variant="body2" sx={{ opacity: 0.8 }}>
                          Formula: Σ(Implied PD × Weight) for all components
                        </Typography>
                      </CardContent>
                    </Card>
                  </Box>
                </Fade>
              )}
            </Box>
          )}

          {/* Step 4: Judgmental Adjustment */}
          {activeStep === 3 && (() => {
            // Mark step 4 as completed when entering (judgmental adjustment is always available)
            if (!stepCompleted[3]) {
              markStepCompleted(3);
            }
            return (
            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                <VerifiedUser sx={{ mr: 1, verticalAlign: 'middle' }} />
                Step 4: Apply Judgmental Adjustment
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
                Apply expert judgment to adjust the final PD calculation based on qualitative factors
              </Typography>

              <Grid container spacing={4} justifyContent="center">
                <Grid item xs={12} md={8}>
                  <Card
                    sx={{
                      p: 4,
                      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                      color: 'white',
                      textAlign: 'center'
                    }}
                  >
                    <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mx: 'auto', mb: 3, width: 80, height: 80 }}>
                      <VerifiedUser sx={{ fontSize: 40 }} />
                    </Avatar>

                    <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                      Judgmental Override
                    </Typography>

                    <Typography variant="body1" sx={{ opacity: 0.9, mb: 4 }}>
                      Apply expert judgment to adjust the calculated PD based on qualitative factors
                      not captured in the quantitative model.
                    </Typography>

                    <TextField
                      select
                      label="Judgmental Factor"
                      value={judgmentalValue}
                      onChange={(e) => handleJudgmentalChange(parseInt(e.target.value))}
                      fullWidth
                      sx={{
                        maxWidth: 300,
                        mx: 'auto',
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                          '&.MuiFocused fieldset': { borderColor: 'white' }
                        },
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.8)' },
                        '& .MuiSelect-icon': { color: 'rgba(255,255,255,0.8)' }
                      }}
                      SelectProps={{
                        MenuProps: {
                          PaperProps: {
                            sx: { backgroundColor: 'white' }
                          }
                        }
                      }}
                    >
                      <MenuItem value={1}>😐 Neutral</MenuItem>
                      <MenuItem value={2}>😊 Positive</MenuItem>
                      <MenuItem value={3}>😟 Negative</MenuItem>
                    </TextField>

                    <TextField
                      type="number"
                      label="Judgment Weight (%)"
                      value={judgmentWeight}
                      onChange={(e) => handleJudgmentWeightChange(e.target.value)}
                      fullWidth
                      sx={{
                        maxWidth: 300,
                        mx: 'auto',
                        mb: 3,
                        '& .MuiOutlinedInput-root': {
                          backgroundColor: 'rgba(255,255,255,0.1)',
                          '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                          '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                          '&.MuiFocused fieldset': { borderColor: 'white' }
                        },
                        '& .MuiInputLabel-root': { color: 'rgba(255,255,255,0.8)' },
                        '& input': { color: 'white', fontFamily: 'monospace' }
                      }}
                      inputProps={{
                        step: 0.1,
                        min: 0,
                        max: 100
                      }}
                      helperText={`Current value: ${judgmentWeight.toFixed(1)}%`}
                      FormHelperTextProps={{
                        sx: { color: 'rgba(255,255,255,0.7)', textAlign: 'center' }
                      }}
                    />

                    <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                      <Typography variant="body2" sx={{ opacity: 0.9 }}>
                        Current Multiplier: <strong>{judgmentalMultiplier}x</strong>
                      </Typography>
                      <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                        {judgmentalValue === 1 && "No adjustment - use calculated PD as-is"}
                        {judgmentalValue === 2 && `Positive adjustment - reduce PD by ${judgmentWeight.toFixed(1)}% for favorable qualitative factors`}
                        {judgmentalValue === 3 && `Negative adjustment - increase PD by ${judgmentWeight.toFixed(1)}% for adverse qualitative factors`}
                      </Typography>
                    </Paper>
                  </Card>
                </Grid>
              </Grid>
            </Box>
            );
          })()}

          {/* Step 5: Results */}
          {activeStep === 4 && (
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: 'primary.main' }}>
                <BarChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                Step 5: Risk Assessment Results
              </Typography>

              {!riskFactors.length ? (
                <Card sx={{ p: 6, textAlign: 'center', background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)' }}>
                  <Analytics sx={{ fontSize: 80, color: 'primary.main', mb: 2, opacity: 0.7 }} />
                  <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
                    Ready to Calculate
                  </Typography>
                  <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
                    Click the button below to perform your risk assessment calculation
                  </Typography>
                  <Button
                    variant="contained"
                    size="large"
                    onClick={calculateImpliedPDs}
                    disabled={calculating}
                    startIcon={calculating ? <CircularProgress size={20} /> : <PlayArrow />}
                    sx={{
                      px: 4,
                      py: 2,
                      fontSize: '1.1rem',
                      borderRadius: 3,
                      boxShadow: theme.shadows[6],
                      '&:hover': {
                        transform: 'translateY(-2px)',
                        boxShadow: theme.shadows[10]
                      },
                      transition: 'all 0.3s ease'
                    }}
                  >
                    {calculating ? 'Analyzing Risk Factors...' : 'Calculate Implied PD'}
                  </Button>
                </Card>
              ) : (
                <Fade in timeout={1000}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                    {/* Final Assessment - Full Width */}
                    <Card
                      sx={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white'
                      }}
                    >
                      <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
                          <Avatar sx={{ bgcolor: 'rgba(255,255,255,0.2)', mr: 3, width: 60, height: 60 }}>
                            <Speed sx={{ fontSize: 30 }} />
                          </Avatar>
                          <Box>
                            <Typography variant="h5" sx={{ fontWeight: 700, mb: 1 }}>
                              FINAL ASSESSMENT
                            </Typography>
                            <Typography variant="body1" sx={{ opacity: 0.9 }}>
                              Probability of Default Calculation
                            </Typography>
                          </Box>
                        </Box>

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                              <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                                IMPLIED PD
                              </Typography>
                              <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'monospace', color: 'primary.main' }}>
                                {(totalWeightedPd * 100).toFixed(4)}%
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                                Raw: {totalWeightedPd.toFixed(6)}
                              </Typography>
                            </Paper>
                          </Grid>
                          <Grid item xs={12} sm={6}>
                            <Paper sx={{ p: 3, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2 }}>
                              <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                                ADJUSTED PD
                              </Typography>
                              <Typography variant="h4" sx={{ fontWeight: 700, fontFamily: 'monospace', color: 'secondary.main' }}>
                                {(finalPd * 100).toFixed(4)}%
                              </Typography>
                              <Typography variant="caption" sx={{ opacity: 0.7, display: 'block', mt: 1 }}>
                                Raw: {finalPd.toFixed(6)}
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 4, bgcolor: 'rgba(255,255,255,0.3)' }} />

                        <Box sx={{ textAlign: 'center' }}>
                          <Typography variant="h6" sx={{ opacity: 0.9, mb: 2 }}>
                            FINAL IMPLIED PROBABILITY OF DEFAULT
                          </Typography>
                          <Typography
                            variant="h2"
                            sx={{
                              fontWeight: 900,
                              fontFamily: 'monospace',
                              textShadow: '2px 2px 4px rgba(0,0,0,0.3)',
                              mb: 1
                            }}
                          >
                            {(finalPd * 100).toFixed(4)}%
                          </Typography>
                          <Typography variant="body2" sx={{ opacity: 0.8 }}>
                            Raw value: {finalPd.toFixed(6)}
                          </Typography>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Rating Dashboard - Full Width */}
                    <Card>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 3 }}>
                          <BarChart sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Rating Dashboard
                        </Typography>

                        <Box sx={{ overflow: 'auto' }}>
                          <TableContainer component={Paper} sx={{ maxHeight: 600 }}>
                            <Table stickyHeader>
                              <TableHead>
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100' }}>NO</TableCell>
                                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100' }}>SCORE COMPONENTS</TableCell>
                                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100' }}>CONTROLS</TableCell>
                                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100' }}>PROFILE</TableCell>
                                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100' }}>RATING VALUES</TableCell>
                                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100' }}>COMPONENT WEIGHTS</TableCell>
                                  <TableCell sx={{ fontWeight: 600, backgroundColor: 'grey.100' }}>IMPLIED PDs</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {riskFactors.map((factor, index) => {
                                  // Get category using dynamic mapping from API categories
                                  const category = findCategoryForComponent(factor.component);
                                  const categoryName = category?.name || factor.display_name;

                                  const selectedValue = selectedValues[categoryName] || '';
                                  const selectedItem = category?.items.find(item => item.name === selectedValue);

                                  // Dynamic display values
                                  const displayName = factor.display_name;
                                  const control = selectedValue || '';
                                  const profile = selectedValue || '';
                                  const ratingValue = selectedItem?.code || '';

                                  return (
                                    <TableRow key={factor.component} hover>
                                      <TableCell>{index + 1}</TableCell>
                                      <TableCell sx={{ fontWeight: 500 }}>{displayName}</TableCell>
                                      <TableCell>{control}</TableCell>
                                      <TableCell>{profile}</TableCell>
                                      <TableCell>{ratingValue}</TableCell>
                                      <TableCell sx={{ fontFamily: 'monospace' }}>{(factor.weight * 100).toFixed(3)}%</TableCell>
                                      <TableCell sx={{ fontFamily: 'monospace' }}>{(factor.implied_pd * 100).toFixed(3)}%</TableCell>
                                    </TableRow>
                                  );
                                })}
                                {/* Judgment Row - Dynamic based on Step 4 selection */}
                                <TableRow>
                                  <TableCell sx={{ fontWeight: 600 }}>{riskFactors.length + 1}</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>JUDGEMENT</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>{judgmentalValue === 1 ? 'Neutral' : judgmentalValue === 2 ? 'Positive' : 'Negative'}</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>{judgmentalValue === 1 ? 'Neutral' : judgmentalValue === 2 ? 'Positive' : 'Negative'}</TableCell>
                                  <TableCell sx={{ fontWeight: 600 }}>{judgmentalValue}</TableCell>
                                  <TableCell sx={{ fontFamily: 'monospace', fontWeight: 600 }}>{judgmentWeight.toFixed(2)}</TableCell>
                                  <TableCell></TableCell>
                                </TableRow>
                                {/* Results Rows */}
                                <TableRow sx={{ backgroundColor: 'primary.light', '& td': { fontWeight: 600, fontFamily: 'monospace' } }}>
                                  <TableCell colSpan={5}></TableCell>
                                  <TableCell sx={{ color: 'primary.contrastText' }}>IMPLIED PD</TableCell>
                                  <TableCell sx={{ color: 'primary.contrastText' }}>{(totalWeightedPd * 100).toFixed(10)}%</TableCell>
                                </TableRow>
                                <TableRow sx={{ backgroundColor: 'secondary.light', '& td': { fontWeight: 600, fontFamily: 'monospace' } }}>
                                  <TableCell colSpan={5}></TableCell>
                                  <TableCell sx={{ color: 'secondary.contrastText' }}>ADJUSTED PD</TableCell>
                                  <TableCell sx={{ color: 'secondary.contrastText' }}>{(finalPd * 100).toFixed(10)}%</TableCell>
                                </TableRow>
                              </TableBody>
                            </Table>
                          </TableContainer>
                        </Box>
                      </CardContent>
                    </Card>

                    {/* Assessment Summary - Full Width */}
                    <Card sx={{ background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)', color: 'white' }}>
                      <CardContent sx={{ p: 3 }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
                          <Timeline sx={{ mr: 1, verticalAlign: 'middle' }} />
                          Assessment Summary
                        </Typography>
                        <Divider sx={{ mb: 2, bgcolor: 'rgba(255,255,255,0.3)' }} />

                        <Grid container spacing={3}>
                          <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                                Risk Factors
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                {categories.length}
                              </Typography>
                            </Paper>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                                Components
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                {riskFactors.length}
                              </Typography>
                            </Paper>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                                Judgmental Factor
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                {judgmentalValue === 1 ? 'Neutral' : judgmentalValue === 2 ? 'Positive' : 'Negative'}
                              </Typography>
                            </Paper>
                          </Grid>

                          <Grid item xs={12} sm={6} md={3}>
                            <Paper sx={{ p: 2, bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, textAlign: 'center' }}>
                              <Typography variant="body2" sx={{ opacity: 0.8, mb: 1 }}>
                                Total Weight
                              </Typography>
                              <Typography variant="h5" sx={{ fontWeight: 700, fontFamily: 'monospace' }}>
                                {(Object.values(componentWeights).reduce((sum, w) => sum + w, 0) * 100).toFixed(1)}%
                              </Typography>
                            </Paper>
                          </Grid>
                        </Grid>

                        <Divider sx={{ my: 3, bgcolor: 'rgba(255,255,255,0.3)' }} />

                        <Accordion sx={{ boxShadow: theme.shadows[2], bgcolor: 'rgba(255,255,255,0.1)' }}>
                          <AccordionSummary expandIcon={<ExpandMore />} sx={{ color: 'white' }}>
                            <Typography sx={{ fontWeight: 600 }}>
                              <Analytics sx={{ mr: 1, verticalAlign: 'middle' }} />
                              Methodology
                            </Typography>
                          </AccordionSummary>
                          <AccordionDetails sx={{ color: 'white' }}>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              This assessment uses multivariate linear regression analysis
                              combined with expert judgmental adjustments.
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              <strong>Formula:</strong> PD = intercept + coefficient × control_value
                            </Typography>
                            <Typography variant="body2" sx={{ mb: 2 }}>
                              <strong>Weighting:</strong> Final PD = Σ(weighted_PDs) × judgmental_multiplier
                            </Typography>
                            <Typography variant="body2">
                              Coefficients are dynamically calculated from the latest master data analysis.
                            </Typography>
                          </AccordionDetails>
                        </Accordion>
                      </CardContent>
                    </Card>
                  </Box>
                </Fade>
              )}
            </Box>
          )}
        </Box>
      </Grow>

      {/* Navigation Buttons */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 6, mb: 4 }}>
        <Button
          disabled={activeStep === 0}
          onClick={handleBack}
          variant="outlined"
          startIcon={<Refresh />}
        >
          Previous
        </Button>

        <Box>
          {activeStep < steps.length - 1 ? (
            <Button
              variant="contained"
              onClick={handleNext}
              endIcon={<PlayArrow />}
              sx={{ ml: 2 }}
            >
              Next Step
            </Button>
          ) : riskFactors.length > 0 ? (
            <Button
              variant="contained"
              onClick={calculateImpliedPDs}
              startIcon={<Calculate />}
              sx={{
                ml: 2,
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: theme.shadows[8]
                },
                transition: 'all 0.3s ease'
              }}
            >
              Recalculate
            </Button>
          ) : null}
        </Box>
      </Box>
    </Container>
  );
};

export default RiskAssessment;
